import {
  Box, Card, CardContent, Chip, IconButton, InputAdornment, Modal, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Add as AddIcon, Close as CloseIcon, Edit as EditIcon, ExitToApp as RetiroIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import CustomButton from '../../../../../../components/General/CustomButton';
import CustomTable from '../../../../../../components/General/CustomTable';
import EmptyTable from '../../../../../../components/General/EmptyTable';
import useStyles from '../../../styles';
import { CustomNumericTextField, CustomSelectTextField, CustomTextField } from '../../../../../../components/General/CustomTextfields';
import { ProgramData, SubjectData } from '../../../../../Program/ProgramListView/types';
import { getPrograms } from '../../../../../../requests/api/sections';
import { getSubjects } from '../../../../../../requests/api/subjects';
import handleApiResponse from '../../../../../../utils/handleApiResponse';
import { ReduxState } from '../../../../../../types';
import { resetAddProgramModalValues, setAddProgramModalValues, setNewContract } from '../../../../../../actions/studentContractActions';
import { formatCcy, thousandSeparator } from '../../../../../../utils/helpers';
import { StudentProgramTableData } from '../../../types';
import { putStudentProgramRetiro } from '../../../../../../requests/api/students';

type ProgramInfo = {
  id: string; name: string; fee: string; startDate: string; endDate: string;
};

type ModalSelectValues = {
  subjectValues: Array<{ id: string; name: string; }>;
  programValues: ProgramInfo[];
};

type RetiroModalState = {
  open: boolean;
  studentProgramId: string;
  programName: string;
  feeRetained: number;
  maxFeeRetained: number;
  isEdit: boolean;
};

/**
 * Plazo: hasta inclusive el día de término del programa, no después.
 * Misma regla que el servidor (isAfterProgramRetiroDeadline).
 */
function isStudentProgramRetiroWindowOpen(programEndDate: string | Date | undefined): boolean {
  if (programEndDate == null || programEndDate === '') return false;
  const end = moment(programEndDate).startOf('day');
  if (!end.isValid()) return false;
  return !moment().startOf('day').isAfter(end, 'day');
}

function retiroDisabledReason(row: StudentProgramTableData): string | null {
  if ((row.retiroMaxFee ?? 0) <= 0) {
    return 'No se puede registrar retiro si el arancel final del programa es cero.';
  }
  if (!isStudentProgramRetiroWindowOpen(row.programEndDate)) {
    return 'El plazo para registrar el retiro venció (el programa ya finalizó).';
  }
  return null;
}

function Programs({
  headers,
  data,
  academicYear,
  contractId,
  onRefresh,
}: {
  headers: { label: string, key: string, valueType: string, }[]
  data: { values: StudentProgramTableData[], count: number },
  academicYear?: number,
  contractId?: string,
  onRefresh?: () => void,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { id: studentId } = useParams<{ id: string }>();
  const location = useLocation();
  const {
    contract,
    contractAddProgramModal,
  } = useSelector((state: ReduxState) => state.studentContract);
  const year = academicYear ?? new Date().getFullYear();
  const [modalSelectValues, setModalSelectValues] = useState<ModalSelectValues>({
    subjectValues: [],
    programValues: [],
  });
  const [retiroModal, setRetiroModal] = useState<RetiroModalState>({
    open: false,
    studentProgramId: '',
    programName: '',
    feeRetained: 0,
    maxFeeRetained: 0,
    isEdit: false,
  });

  useEffect(() => {
    // I need to do it on a separate call, as I need ALL the information,
    // not only the one available for the created sections
    const getSubjectsForFields = async () => {
      let subjectsInfo: { id: string; name: string; }[];

      // Get subjects
      try {
        const { subjects } = await getSubjects();
        subjectsInfo = subjects.map((elem: SubjectData) => ({ id: elem.id, name: elem.name }));
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }

      setModalSelectValues((prevState) => ({ ...prevState, subjectValues: subjectsInfo }));
    };

    getSubjectsForFields();
  }, []);

  useEffect(() => {
    const getProgramValuesForFields = async () => {
      let programInfo: ProgramInfo[];
      // Get programs filtered by academic year
      if (contractAddProgramModal && contractAddProgramModal.values
        && contractAddProgramModal.values.subjectId) {
        try {
          const { programs } = await getPrograms(
            contractAddProgramModal.values.subjectId,
            year,
          );
          programInfo = programs.map((elem: ProgramData) => ({
            id: elem.id,
            name: elem.name,
            fee: elem.fee,
            startDate: elem.startDate ? elem.startDate.toString() : '',
            endDate: elem.endDate ? elem.endDate.toString() : '',
          }));

          if (contractAddProgramModal.values.subjectId) {
            setModalSelectValues((prevState) => ({ ...prevState, programValues: programInfo }));
          }
        } catch (err) {
          const e = err as AxiosError;
          handleApiResponse(enqueueSnackbar, e, false);
        }
      }
    };

    getProgramValuesForFields();
  }, [
    contractAddProgramModal?.values?.subjectId,
    year,
  ]);

  const handleAddProgramChange = (
    e: React.ChangeEvent<HTMLInputElement> | Date | File, source: string,
  ) => {
    const { value } = (e as React.ChangeEvent<HTMLInputElement>).target;
    if (source === 'feeDiscount') {
      const finalValue = `${Number(value)}`;
      dispatch(setAddProgramModalValues({
        ...contractAddProgramModal,
        values: {
          ...contractAddProgramModal.values,
          [source]: finalValue,
        },
      }));
    } else {
      dispatch(setAddProgramModalValues({
        ...contractAddProgramModal,
        values: {
          ...contractAddProgramModal.values,
          [source]: value,
        },
      }));
    }
  };

  const handleCloseModal = () => {
    dispatch(resetAddProgramModalValues());
  };

  const handleAddEditProgram = () => {
    const programIdToAdd = contractAddProgramModal.values.selectedProgramId;
    const subjectInfo = modalSelectValues.subjectValues
      .filter((elem) => elem.id === contractAddProgramModal.values.subjectId);
    let programFee;
    let programName;
    let programStartDate;
    let programEndDate;
    modalSelectValues.programValues.forEach((elem) => {
      if (elem.id === contractAddProgramModal.values.selectedProgramId) {
        programFee = elem.fee;
        programName = elem.name;
        programStartDate = elem.startDate;
        programEndDate = elem.endDate;
      }
    });

    if (contractAddProgramModal.values.selectedProgramIdToEdit) {
      const filteredStudentPrograms = contract![studentId].selectedStudentPrograms!
        .filter((elem) => elem.programId !== contractAddProgramModal
          .values.selectedProgramIdToEdit);
      const auxContractInfo = {
        ...contract![studentId],
        selectedStudentPrograms: [...filteredStudentPrograms, {
          programId: programIdToAdd,
          subjectId: subjectInfo[0].id,
          subjectName: subjectInfo[0].name,
          programName: programName ?? '',
          programFee: programFee ?? '',
          programFeeDiscount: contractAddProgramModal.values.feeDiscount ?? 0,
          comment: contractAddProgramModal.values.comment,
          programStartDate: programStartDate ?? '',
          programEndDate: programEndDate ?? '',
        }],
      };
      dispatch(setNewContract({ studentId, studentContract: auxContractInfo }));
    } else if (contract && contract[studentId]) {
      if (contract[studentId].selectedStudentPrograms) {
        if (!contract[studentId].selectedStudentPrograms!
          .some((elem) => elem.programId === contractAddProgramModal.values.selectedProgramId)) {
          const auxContractInfo = {
            ...contract[studentId],
            selectedStudentPrograms: [...contract[studentId].selectedStudentPrograms!, {
              programId: programIdToAdd,
              subjectId: subjectInfo[0].id,
              subjectName: subjectInfo[0].name,
              programName: programName ?? '',
              programFee: programFee ?? '',
              programFeeDiscount: contractAddProgramModal.values.feeDiscount ?? 0,
              comment: contractAddProgramModal.values.comment,
              programStartDate: programStartDate ?? '',
              programEndDate: programEndDate ?? '',
            }],
          };
          dispatch(setNewContract({ studentId, studentContract: auxContractInfo }));
        } else {
          enqueueSnackbar('Programa ya ha sido agregado. Editar la opcion existente', { variant: 'error' });
        }
      } else {
        const auxContractInfo = {
          ...contract[studentId],
          selectedStudentPrograms: [{
            programId: programIdToAdd,
            subjectId: subjectInfo[0].id,
            subjectName: subjectInfo[0].name,
            programName: programName ?? '',
            programFee: programFee ?? '',
            programFeeDiscount: contractAddProgramModal.values.feeDiscount ?? 0,
            comment: contractAddProgramModal.values.comment,
            programStartDate: programStartDate ?? '',
            programEndDate: programEndDate ?? '',
          }],
        };
        dispatch(setNewContract({ studentId, studentContract: auxContractInfo }));
      }
    } else {
      dispatch(setNewContract({
        studentId,
        studentContract: {
          selectedStudentPrograms: [{
            programId: programIdToAdd,
            subjectId: subjectInfo[0].id,
            subjectName: subjectInfo[0].name,
            programName: programName ?? '',
            programFee: programFee ?? '',
            programFeeDiscount: contractAddProgramModal.values.feeDiscount ?? 0,
            comment: contractAddProgramModal.values.comment,
            programStartDate: programStartDate ?? '',
            programEndDate: programEndDate ?? '',
          }],
        },
      }));
    }
    dispatch(resetAddProgramModalValues());
  };

  const isEditProgram = Boolean(contractAddProgramModal && contractAddProgramModal.values
    && contractAddProgramModal.values?.selectedProgramIdToEdit);

  const handleConfirmRetiro = async () => {
    if (!contractId || !retiroModal.studentProgramId) return;
    const max = retiroModal.maxFeeRetained;
    if (max <= 0) {
      enqueueSnackbar('No se puede registrar retiro con arancel final cero.', { variant: 'error' });
      return;
    }
    const fee = Math.min(Math.max(0, retiroModal.feeRetained), max);
    try {
      const response = await putStudentProgramRetiro(
        contractId,
        retiroModal.studentProgramId,
        fee,
      );
      handleApiResponse(enqueueSnackbar, response, true);
      setRetiroModal({
        open: false, studentProgramId: '', programName: '', feeRetained: 0, maxFeeRetained: 0, isEdit: false,
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const closeRetiroModal = () => setRetiroModal({
    open: false, studentProgramId: '', programName: '', feeRetained: 0, maxFeeRetained: 0, isEdit: false,
  });

  const retiroColumn = {
    label: 'Acción',
    key: 'retiroAction',
    valueType: 'custom',
    functionToApply: (_: unknown, __: number, row: StudentProgramTableData) => {
      if (!contractId) return null;
      if (row.isRetired) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label='Retirado' color='warning' size='small' />
            <IconButton
              size='small'
              title='Editar monto de retiro'
              onClick={() => {
                setRetiroModal({
                  open: true,
                  studentProgramId: row.id ?? '',
                  programName: row.programName,
                  feeRetained: row.feeRetained ?? 0,
                  maxFeeRetained: row.retiroMaxFee ?? 0,
                  isEdit: true,
                });
              }}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Box>
        );
      }
      const disableReason = retiroDisabledReason(row);
      const button = (
        <span>
          <IconButton
            size='small'
            title='Registrar retiro'
            disabled={disableReason != null}
            onClick={() => {
              if (disableReason) return;
              setRetiroModal({
                open: true,
                studentProgramId: row.id ?? '',
                programName: row.programName,
                feeRetained: 0,
                maxFeeRetained: row.retiroMaxFee ?? 0,
                isEdit: false,
              });
            }}
          >
            <RetiroIcon fontSize='small' />
          </IconButton>
        </span>
      );
      if (disableReason) {
        return (
          <Tooltip title={disableReason}>
            {button}
          </Tooltip>
        );
      }
      return button;
    },
  };

  const headersWithRetiro = contractId ? [...headers, retiroColumn] : headers;

  return (
    <Box className={classes.contractViewProgramsContainer}>
      <Box className={classes.contractViewProgramsTitleButtonContainer}>
        <Box className={classes.contractViewProgramsTitleContainer}>
          <Typography
            variant='h3'
            color='textPrimary'
            fontWeight='bold'
          >
            Programas
          </Typography>
        </Box>
        <Box className={classes.contractViewProgramsButtonContainer}>
          {location.pathname.includes('contrato') && <CustomButton
            colorType='tertiary'
            text='Agregar programa'
            icon={<AddIcon />}
            onClick={() => dispatch(setAddProgramModalValues({
              open: true,
            }))}
          />}

        </Box>
      </Box>
      <Box className={classes.contractViewProgramsTableContainer}>
        {data.count !== 0
          && <CustomTable
            headers={headersWithRetiro}
            data={data}
          />}
        {data.count === 0 && <EmptyTable />}
      </Box>
      <Modal
        open={retiroModal.open}
        onClose={closeRetiroModal}
      >
        <Box className={classes.modalContainer}>
          <Card className={classes.modal}>
            <CardContent className={classes.modalContent}>
              <Box className={classes.modalTitleContainer}>
                <Box className={classes.modalTitleAndIcon}>
                  <RetiroIcon color='primary' />
                  <Typography variant='h4' color='primary' fontWeight='bold'>
                    {`${retiroModal.isEdit ? 'Editar retiro' : 'Registrar retiro'}: ${retiroModal.programName}`}
                  </Typography>
                </Box>
                <IconButton onClick={closeRetiroModal}>
                  <CloseIcon color='primary' />
                </IconButton>
              </Box>
              <Box className={classes.modalTextfieldsContainer}>
                <Typography variant='body1' color='textPrimary' sx={{ mb: 2 }}>
                  Ingrese el monto que retiene la institución por este programa.
                  El saldo restante se registrará como devolución. El monto no puede superar el
                  arancel final (
                  {formatCcy(String(retiroModal.maxFeeRetained), '.')}
                  ).
                </Typography>
                <Box className={classes.modalIndividualTextfield}>
                  <TextField
                    fullWidth
                    label='Monto retenido'
                    variant='outlined'
                    color='secondary'
                    value={thousandSeparator(retiroModal.feeRetained, '.')}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '');
                      const val = digitsOnly === '' ? 0 : Number(digitsOnly);
                      const max = retiroModal.maxFeeRetained;
                      const clamped = Math.min(Math.max(0, val), max);
                      setRetiroModal((prev) => ({ ...prev, feeRetained: clamped }));
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9.]*' }}
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                    }}
                  />
                </Box>
              </Box>
              <Box className={classes.modalButtonsContainer}>
                <CustomButton
                  text='Cancelar'
                  colorType='tertiary'
                  onClick={closeRetiroModal}
                  icon={<CloseIcon />}
                />
                <CustomButton
                  text={retiroModal.isEdit ? 'Guardar cambios' : 'Confirmar retiro'}
                  colorType='primary'
                  onClick={handleConfirmRetiro}
                  icon={retiroModal.isEdit ? <EditIcon /> : <RetiroIcon />}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Modal>
      <Modal
        open={contractAddProgramModal.open}
        onClose={handleCloseModal}
      >
        <Box className={classes.modalContainer}>
          <Card className={classes.modal}>
            <CardContent className={classes.modalContent}>
              <Box className={classes.modalTitleContainer}>
                <Box className={classes.modalTitleAndIcon}>
                  {isEditProgram ? <EditIcon color='primary' /> : <AddIcon color='primary' />}

                  <Typography variant='h4' color='primary' fontWeight='bold'>{isEditProgram ? 'Editar programa' : 'Agregar programa'} </Typography>
                </Box>
                <IconButton onClick={handleCloseModal}>
                  <CloseIcon color='primary' />
                </IconButton>
              </Box>
              <Box className={classes.modalTextfieldsContainer}>
                <Box className={classes.modalIndividualTextfield}>
                  <CustomSelectTextField
                    disabled={isEditProgram}
                    keyField={'subjectId'}
                    name={'Asignatura'}
                    value={(contractAddProgramModal && contractAddProgramModal.values
                      && contractAddProgramModal.values.subjectId)
                      ? modalSelectValues.subjectValues.filter((elem) => elem.id === contractAddProgramModal.values.subjectId)[0].id : ''}
                    onChangeFunction={handleAddProgramChange}
                    selectFields={modalSelectValues.subjectValues}
                    valueToRetrieveFromSelect='id'
                  />
                </Box>
                <Box className={classes.modalIndividualTextfield}>
                  <CustomSelectTextField
                    disabled={isEditProgram}
                    keyField={'selectedProgramId'}
                    name={'Programa'}
                    value={(contractAddProgramModal && contractAddProgramModal.values && contractAddProgramModal.values.selectedProgramId) ?? ''}
                    onChangeFunction={handleAddProgramChange}
                    selectFields={modalSelectValues.programValues}
                    valueToRetrieveFromSelect='id'
                  />
                </Box>
                <Box className={classes.modalIndividualTextfield}>
                  <CustomNumericTextField
                    keyField={'feeDiscount'}
                    name={'Monto descuento arancel del programa'}
                    value={(contractAddProgramModal && contractAddProgramModal.values && contractAddProgramModal.values.feeDiscount) ?? '0'}
                    onChangeFunction={handleAddProgramChange}
                  />
                </Box>
                <Box className={classes.modalIndividualTextfield}>
                  <CustomTextField
                    keyField={'comment'}
                    name={'Comentario (opcional)'}
                    value={(contractAddProgramModal && contractAddProgramModal.values && contractAddProgramModal.values.comment) ?? ''}
                    onChangeFunction={handleAddProgramChange}
                  />
                </Box>
                <Box className={classes.modalIndividualTextfield} flexDirection='column'>
                  <Box className={classes.modalIndividualTextfieldTypographyPrice}>
                    <Typography variant='h4' color='primary' fontWeight='bold'>Precio lista arancel: </Typography>
                    <Typography variant='h4' color='textBody'>{contractAddProgramModal?.values?.selectedProgramId
                      ? formatCcy(modalSelectValues.programValues.find((elem) => elem.id === contractAddProgramModal.values.selectedProgramId)?.fee ?? '0', '.')
                      : '$ 0'
                    }</Typography>
                  </Box>
                  <Box className={classes.modalIndividualTextfieldTypographyPrice}>
                    <Typography variant='h4' color='primary' fontWeight='bold'>Precio descuento arancel: </Typography>
                    <Typography variant='h4' color='textBody'>{contractAddProgramModal?.values?.selectedProgramId
                      ? formatCcy((parseInt(modalSelectValues.programValues.find((elem) => elem.id === contractAddProgramModal.values.selectedProgramId)?.fee ?? '0', 10) - (parseInt(contractAddProgramModal?.values?.feeDiscount ?? 0, 10))), '.')
                      : '$ 0'}</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className={classes.modalButtonsContainer}>
                <CustomButton text='Cancelar' colorType='tertiary' onClick={handleCloseModal} key={'CancelButtonAddProgram'} icon={<CloseIcon />} />
                <CustomButton text={isEditProgram ? 'Editar programa' : 'Agregar programa'} colorType='primary' onClick={handleAddEditProgram} key={'AcceptButtonAddProgram'} icon={<AddIcon />} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </Box>
  );
}

export default Programs;
